import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Images, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { imgSrc } from "@/lib/constants";

function Lightbox({ project, index, onClose, onIndex }) {
  const images = project.images || [];

  const next = useCallback(() => onIndex((i) => (i + 1) % images.length), [images.length, onIndex]);
  const prev = useCallback(() => onIndex((i) => (i - 1 + images.length) % images.length), [images.length, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [next, prev, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex flex-col bg-ink-950/95 backdrop-blur-md"
      onClick={onClose}
      data-testid={`lightbox-${project.slug}`}
    >
      <div className="flex items-center justify-between px-5 py-4" onClick={(e) => e.stopPropagation()}>
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-paper/70" data-testid="lightbox-counter">
          {index + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          className="flex h-11 w-11 items-center justify-center border border-white/20 text-paper transition-colors hover:border-copper hover:text-copper"
          aria-label="Închide galeria"
          data-testid="lightbox-close-btn"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <motion.img
            key={index}
            src={imgSrc(images[index])}
            alt={`${project.title || project.category} — poza ${index + 1}`}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragEnd={(e, info) => {
              if (info.offset.x < -60) next();
              if (info.offset.x > 60) prev();
            }}
            className="max-h-[72vh] w-auto max-w-full cursor-grab select-none object-contain active:cursor-grabbing"
            data-testid="lightbox-image"
          />
        </AnimatePresence>

        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center border border-white/20 bg-ink-950/70 text-paper backdrop-blur transition-colors hover:border-copper hover:text-copper"
              aria-label="Poza anterioară"
              data-testid="lightbox-prev-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              className="absolute right-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center border border-white/20 bg-ink-950/70 text-paper backdrop-blur transition-colors hover:border-copper hover:text-copper"
              aria-label="Poza următoare"
              data-testid="lightbox-next-btn"
            >
              <ArrowRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-5 py-4" onClick={(e) => e.stopPropagation()} data-testid="lightbox-thumbs">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => onIndex(i)}
              className={`h-14 w-20 shrink-0 overflow-hidden border transition-all ${i === index ? "border-copper opacity-100" : "border-white/10 opacity-50 hover:opacity-80"}`}
              data-testid={`lightbox-thumb-${i}`}
            >
              <img src={imgSrc(img)} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
}

export default function PortfolioGrid({ projects }) {
  const [open, setOpen] = useState(null);

  return (
    <>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="portfolio-grid">
        {projects.map((p, i) => {
          const cover = p.cover || p.images?.[0] || "";
          const title = p.title || p.category || "Proiect";
          return (
            <button
              key={p.slug || p._id}
              onClick={() => setOpen({ project: p, index: 0 })}
              className="card-brox group relative overflow-hidden text-left hover:border-copper/50"
              data-testid={`portfolio-card-${p.slug || i}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-ink-800">
                <img
                  src={imgSrc(cover)}
                  alt={title ? `${title} — Brox Construct` : "Proiect Brox Construct"}
                  loading={i < 3 ? "eager" : "lazy"}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/90 via-ink-950/10 to-transparent" aria-hidden="true" />
                <span className="absolute left-4 top-4 border border-copper/50 bg-ink-950/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-copper backdrop-blur">
                  {p.category || "Proiect"}
                </span>
                {p.images?.length > 1 && (
                  <span className="absolute right-4 top-4 flex items-center gap-1.5 border border-white/20 bg-ink-950/70 px-2.5 py-1 font-mono text-[10px] text-paper/80 backdrop-blur">
                    <Images className="h-3.5 w-3.5" />
                    {p.images.length}
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <h3 className="display-title text-2xl text-paper">{title}</h3>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-copper opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    DESCHIDE GALERIA
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <AnimatePresence>
        {open && (
          <Lightbox
            project={open.project}
            index={open.index}
            onIndex={setOpen ? (fn) => setOpen((o) => ({ ...o, index: fn(o.index) })) : undefined}
            onClose={() => setOpen(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
